import React, {useCallback, useEffect, useState} from "react";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {routeFunctions} from "../../../../core/models/static/routes/app-routes";
import usePartnerConfiguration from "../../../hooks/use-partner-configuration";
import AssignUserToCustomerDialogExistingCustomersSection from "./sections/existing-customers";
import AssignUserToCustomerDialogSuggestionsSection from "./sections/suggestions";
import AssignUserToCustomerDialogCurrentCustomerSection from "./sections/current-customer";
import {useHistory} from "react-router-dom";


const AssignUserToCustomerDialog = ({
                                        open,
                                        setOpen,
                                        user,
                                        countries,
                                    }) => {
    const history = useHistory();
    const [joining, setJoining] = useState(false);
    const {getValue} = usePartnerConfiguration();
    const [sendEmail, setSendEmail] = useState(getValue((ids) => ids.sendEmailUponCustomerRegistration) ?? false);
    const isMounted = useIsMounted();

    /**
     * With each change in the [open] state of the dialog:
     * - if open, sets the [sendEmail] state value from the partner configuration.
     */
    useEffect(() => {
        if (!open || !user) {
            return
        }
        setSendEmail(getValue((ids) => ids.sendEmailUponCustomerRegistration) ?? false);
    }, [user, open, getValue])

    /**
     * Fetches the customer id by the code and navigates to the profile page.
     * @type {(function(*): Promise<void>)}
     */
    const fetchCustomerIdByCodeAndNavigate = useCallback(async (code) => {
        const response = await Api.getCustomerSimpleInfoByCode(code);
        if (!isMounted())
            return;
        if (response?.resultFlag && response.data) {
            history.push(routeFunctions.customer.generalInformation.profile(response.data.id));
        }
    }, [isMounted, history])

    /**
     * Calls the api for accepting the customer and assigning it to the selected suggestion.
     * @type {function(*=): Promise<void>}
     */
    const acceptAndJoin = useCallback(async (selectedCustomer) => {
        setJoining(true);
        const forAPi = {
            userId: user.id,
            code: selectedCustomer.code,
            dontNotifyUser: !sendEmail,
        }
        const response = await Api.AssignCompanyToUser(forAPi);
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            setJoining(false);
            return;
        }
        await fetchCustomerIdByCodeAndNavigate(forAPi.code);
        if (!isMounted())
            return;
        setJoining(false);
    }, [user?.id, sendEmail, isMounted, fetchCustomerIdByCodeAndNavigate])

    /**
     * Fetches the user profile info and navigates to the profile page.
     * @type {(function(*): Promise<void>)}
     */
    const fetchUserProfileInfoAndNavigate = useCallback(async (userId) => {
        const response = await Api.getUserBasicInfo(userId);
        if (!isMounted())
            return;
        if (response?.resultFlag && response.data) {
            history.push(routeFunctions.customer.generalInformation.profile(response.data.customer.id));
        }
    }, [isMounted, history])

    /**
     * Calls the api for accepting the customer and creating the company for them and then if the api result is
     * successful then the user will be removed form the list.
     */
    const acceptAndCreateCompany = useCallback(async () => {
        setJoining(true);
        const forAPi = {
            userId: user?.id,
            newCustomer: {
                address: user?.address,
                postalCode: user?.postalCode,
                name: user?.company,
                city: user?.city,
                province: user?.province,
            },
            dontNotifyUser: !sendEmail,
        }
        const response = await Api.AssignCompanyToUser(forAPi);
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            setJoining(false);
            return;
        }
        await fetchUserProfileInfoAndNavigate(forAPi.userId);
        if (!isMounted())
            return;
        setJoining(false);
    }, [fetchUserProfileInfoAndNavigate, isMounted, sendEmail, user?.address, user?.city, user?.company, user?.id, user?.postalCode, user?.province])

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            loading={joining}
            disableEscapeKeyDown={joining}
            disableBackdropClick={joining}
            maxWidth={'md'}
            className={'bound-dialog'}
        >
            <DialogTitle>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    {
                        user
                            ? `Assign Customer to ${user.firstName ?? ''} ${user.lastName ?? ''}`
                            : "Assign Customer"
                    }
                </p>
            </DialogTitle>
            <DialogContent dividers className={'p-4'}>
                <AssignUserToCustomerDialogCurrentCustomerSection
                    user={user}
                    countries={countries}
                />
                <div className={'w-100 my-5'}/>
                {
                    !!user?.userCustomerSuggestionsList?.length &&
                    <>
                        <AssignUserToCustomerDialogSuggestionsSection
                            suggestions={user?.userCustomerSuggestionsList}
                            countries={countries}
                            join={acceptAndJoin}
                            submitting={joining}
                        />
                        <div className={'w-100 my-5'}/>
                    </>
                }
                <AssignUserToCustomerDialogExistingCustomersSection
                    countries={countries}
                    join={acceptAndJoin}
                    submitting={joining}
                />
            </DialogContent>
            <DialogActions className={'d-flex align-items-center justify-content-between'} sx={{padding: 0}}>
                <Button
                    disabled={!open || joining}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={() => setOpen(false)}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    disabled={!open || joining}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={acceptAndCreateCompany}
                    borderRadius={'8px 0 8px 0'}
                >
                    {
                        joining
                            ? "Processing..."
                            : "Create new Customer"
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}


export default AssignUserToCustomerDialog;
